import { useCallback, useSyncExternalStore } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { quantApi } from '@/app/lib/quant/quantApi'
import { quantErrorMessage } from '@/app/lib/quant/quantErrors'
import type { Run } from '@/app/lib/quant/types'
import { registerSessionReset } from '@/app/lib/sessionReset'

export type QuantRunsState =
  | { status: 'loading' }
  /** `error` is set while the latest poll failed; the previous runs stay visible. */
  | { status: 'loaded'; runs: Run[]; active: number; max: number; ownActive?: number; error?: string }
  | { status: 'error'; message: string }

const POLL_MS = 2000

/*
 * One module-level store so a run started on one route is visible on every
 * other (spec 036 T4.1). It loads on first subscribe, reloads whenever a new
 * component subscribes, and polls every 2 s ONLY while some run is `running`
 * and someone is subscribed. Timers are `setTimeout` so `page.clock` drives them.
 */
let state: QuantRunsState = { status: 'loading' }
let sequence = 0
let failures = 0
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function publish(next: QuantRunsState): void {
  state = next
  for (const listener of listeners) listener()
}

function clearTimer(): void {
  if (timer !== null) clearTimeout(timer)
  timer = null
}

function load(): Promise<void> {
  clearTimer()
  sequence += 1
  const mine = sequence
  return quantApi
    .listRuns()
    .then((result) => {
      if (mine !== sequence) return
      failures = 0
      publish({
        status: 'loaded',
        runs: result.runs,
        active: result.active,
        max: result.max,
        ...(result.own_active === undefined ? {} : { ownActive: result.own_active }),
      })
      if (listeners.size > 0 && result.runs.some((run) => run.status === 'running')) {
        timer = setTimeout(() => void load(), POLL_MS)
      }
    })
    .catch((error: unknown) => {
      if (mine !== sequence) return
      const message = quantErrorMessage(error)
      failures += 1
      if (state.status === 'loaded') publish({ ...state, error: message })
      else publish({ status: 'error', message })
      // Keep trying with backoff while anyone is looking; stale data stays on screen.
      if (listeners.size > 0) timer = setTimeout(() => void load(), nextDelay(failures))
    })
}

/** Reloads now (after a start or cancel). Safe to call from anywhere. */
export function refreshQuantRuns(): Promise<void> {
  return load()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // Defer so subscribe itself never publishes synchronously during React's commit.
  queueMicrotask(() => {
    if (listeners.has(listener) && (listeners.size === 1 || state.status !== 'loaded')) void load()
  })
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      clearTimer()
      sequence += 1 // drop any in-flight response
    }
  }
}

function getSnapshot(): QuantRunsState {
  return state
}

export function resetQuantRuns(): void {
  clearTimer()
  sequence += 1
  failures = 0
  listeners.clear()
  state = { status: 'loading' }
}

export function useQuantRuns(): QuantRunsState & { retry: () => void } {
  const current = useSyncExternalStore(subscribe, getSnapshot)
  const retry = useCallback(() => {
    publish({ status: 'loading' })
    void load()
  }, [])
  return { ...current, retry }
}

registerSessionReset(resetQuantRuns)
